/**
 * Domain enums, kept in one place so the API, the admin console and the partner
 * portal cannot drift apart. These were untyped varchar columns in the legacy
 * database, which is why values like "BU" and "BSV" both ended up in use.
 */

export const LEAD_TYPES = ['KFZ', 'PHV', 'PKV', 'GKV', 'BSV', 'RS'] as const;
export type LeadType = (typeof LEAD_TYPES)[number];

/** German product names, used for labels in both portals and the mobile app. */
export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  KFZ: 'Kfz-Versicherung',
  PHV: 'Privathaftpflicht',
  PKV: 'Private Krankenversicherung',
  GKV: 'Gesetzliche Krankenversicherung',
  BSV: 'Berufsunfähigkeitsversicherung',
  RS: 'Rechtsschutzversicherung',
};

/**
 * The Flutter app sends "BU" for occupational disability while the database
 * calls it "BSV". The legacy API patched this inline in three separate files;
 * normalise it once here instead.
 */
const LEAD_TYPE_ALIASES: Record<string, LeadType> = {
  BU: 'BSV',
};

export function normalizeLeadType(input: string): LeadType | null {
  const upper = input.trim().toUpperCase();
  const aliased = LEAD_TYPE_ALIASES[upper] ?? upper;
  return (LEAD_TYPES as readonly string[]).includes(aliased) ? (aliased as LeadType) : null;
}

export const PAYMENT_FREQUENCIES = ['YEARLY', 'SEMI_ANNUAL', 'QUARTERLY', 'MONTHLY'] as const;
export type PaymentFrequency = (typeof PAYMENT_FREQUENCIES)[number];

/** Legacy `comp_offers.emi` stored these German strings directly. */
export const PAYMENT_FREQUENCY_LABELS: Record<PaymentFrequency, string> = {
  YEARLY: 'jährlich',
  SEMI_ANNUAL: 'halb-jährlich',
  QUARTERLY: 'quartalsweise',
  MONTHLY: 'monatlich',
};

export const PAYMENT_FREQUENCY_FROM_LEGACY: Record<string, PaymentFrequency> = {
  'jährlich': 'YEARLY',
  'halb-jährlich': 'SEMI_ANNUAL',
  'quartalsweise': 'QUARTERLY',
  'monatlich': 'MONTHLY',
};

/** Payments per year, for normalising quotes to a comparable annual figure. */
export const PAYMENTS_PER_YEAR: Record<PaymentFrequency, number> = {
  YEARLY: 1,
  SEMI_ANNUAL: 2,
  QUARTERLY: 4,
  MONTHLY: 12,
};

export const OFFER_STATUSES = ['PENDING', 'ACCEPTED', 'WITHDRAWN'] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'DECLINED'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const REVIEW_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const INSURED_PARTY_TYPES = ['ADULT', 'CHILD', 'FAMILY'] as const;
export type InsuredPartyType = (typeof INSURED_PARTY_TYPES)[number];

/** Legacy `PKV_Insurance.insure_person` held the German word. */
export const INSURED_PARTY_LABELS: Record<InsuredPartyType, string> = {
  ADULT: 'Erwachsener',
  CHILD: 'Kind',
  FAMILY: 'Familie',
};

export const INSURED_PARTY_FROM_LEGACY: Record<string, InsuredPartyType> = {
  Erwachsener: 'ADULT',
  Kind: 'CHILD',
  Familie: 'FAMILY',
};

export const PLATFORMS = ['ANDROID', 'IOS'] as const;
export type Platform = (typeof PLATFORMS)[number];

export const ACTOR_TYPES = ['USER', 'PARTNER', 'ADMIN', 'SYSTEM'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];
